import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Box, Chip, CircularProgress, Grid, Stack, Typography } from "@mui/material";
import { Timer, KanbanSquare, Tag, Map, ListTodo } from "lucide-react";
import { EmptyState, GlassCard, HoverCard, PageHeader, T, TYPE, formatDate } from "../user/complaints/shared";
import { pm, errorMessage } from "./api";
import { PmPage } from "./PmContext";
import { ProjectBadge, Progress } from "./ui";
import PmTopBar from "./PmTopBar";

// Cross-project entry points from the sidebar (Backlog, Active Sprints, Boards,
// Releases, Roadmaps): each lists what the user can reach and jumps into the
// project tab.

const CONFIG = {
  backlog: { icon: ListTodo, title: "Backlog Planning", subtitle: "Select a project to prioritize issues, manage the product backlog, and plan sprints.", tab: "backlog", filter: (p) => p.sprintsEnabled },
  sprints: { icon: Timer, title: "Active Sprints", subtitle: "Monitor active sprints, sprint goals, velocity pace, and issue progress across projects." },
  boards: { icon: KanbanSquare, title: "Project Boards", subtitle: "Access interactive Kanban and Scrum delivery boards across all workspace projects." },
  releases: { icon: Tag, title: "Releases & Versions", subtitle: "Track version milestones, release timelines, and delivery readiness across projects.", tab: "releases" },
  roadmaps: { icon: Map, title: "Roadmaps", subtitle: "Visualize long-term project timelines, epic delivery schedules, and dependencies.", tab: "roadmap" },
};

const Inner = ({ kind }) => {
  const navigate = useNavigate();
  const cfg = CONFIG[kind];
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    setRows(null);
    const f = kind === "sprints" ? pm.activeSprints() : kind === "boards" ? pm.boards() : pm.projects();
    f.then(setRows).catch((err) => setError(errorMessage(err)));
  }, [kind]);
  const list = rows && cfg.filter ? rows.filter(cfg.filter) : rows;
  const hrefOf = (r) => {
    if (kind === "sprints") return r.boardId ? `/projects/${r.project?.key}/boards/${r.boardId}` : `/projects/${r.project?.key}/board`;
    if (kind === "boards") return `/projects/${r.project?.key}/boards/${r._id}`;
    return `/projects/${r.key}/${cfg.tab}`;
  };
  // Only one thing to pick: open it directly instead of a one-card list.
  const only = list?.length === 1 ? hrefOf(list[0]) : null;
  useEffect(() => {
    if (only) navigate(only, { replace: true });
  }, [only, navigate]);
  if (error) return <Alert severity="error">{error}</Alert>;
  return (
    <>
      <PmTopBar />
      <PageHeader icon={cfg.icon} title={cfg.title} subtitle={cfg.subtitle} />
      {!list || only ? <Box sx={{ py: 8, textAlign: "center" }}><CircularProgress size={24} /></Box> : !list.length ? (
        <GlassCard><EmptyState icon={cfg.icon} title={`No ${cfg.title.toLowerCase()} yet`} /></GlassCard>
      ) : (
        <Grid container spacing={2}>
          {list.map((r) => {
            if (kind === "sprints") {
              const pct = r.totals.scopeCount ? (r.totals.completedCount / r.totals.scopeCount) * 100 : 0;
              return (
                <Grid item key={r._id} xs={12} md={6} lg={4}>
                  <HoverCard sx={{ p: 2, cursor: "pointer" }} onClick={() => navigate(hrefOf(r))}>
                    <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.25 }}>
                      <ProjectBadge project={r.project} size={30} />
                      <Box sx={{ minWidth: 0 }}><Typography noWrap sx={{ fontWeight: 700 }}>{r.name}</Typography><Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted }}>{r.project?.name} · ends {formatDate(r.endDate)}</Typography></Box>
                    </Stack>
                    {r.goal && <Typography sx={{ fontSize: TYPE.small, color: T.inkSecondary, mb: 1 }}>{r.goal}</Typography>}
                    <Progress value={pct} label={`${r.totals.completedCount}/${r.totals.scopeCount} issues · ${r.totals.completedPoints}/${r.totals.scopePoints} pts`} color="#12B76A" />
                  </HoverCard>
                </Grid>
              );
            }
            if (kind === "boards") {
              return (
                <Grid item key={r._id} xs={12} sm={6} lg={4}>
                  <HoverCard sx={{ p: 2, cursor: "pointer" }} onClick={() => navigate(hrefOf(r))}>
                    <Stack direction="row" spacing={1.25} alignItems="center">
                      <ProjectBadge project={r.project} size={30} />
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}><Typography noWrap sx={{ fontWeight: 700 }}>{r.name}</Typography><Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted }}>{r.project?.name}</Typography></Box>
                      <Chip size="small" label={r.type} sx={{ height: 20, fontSize: "0.66rem" }} />
                    </Stack>
                  </HoverCard>
                </Grid>
              );
            }
            return (
              <Grid item key={r._id} xs={12} sm={6} lg={4}>
                <HoverCard sx={{ p: 2, cursor: "pointer" }} onClick={() => navigate(hrefOf(r))}>
                  <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1 }}>
                    <ProjectBadge project={r} size={30} />
                    <Box sx={{ minWidth: 0 }}><Typography noWrap sx={{ fontWeight: 700 }}>{r.name}</Typography><Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted }}>{r.key}{r.stats.activeSprint ? ` · ${r.stats.activeSprint.name}` : ""}</Typography></Box>
                  </Stack>
                  <Progress value={r.stats.progress} label={`${r.stats.open} open · ${r.stats.overdue} overdue`} />
                </HoverCard>
              </Grid>
            );
          })}
        </Grid>
      )}
    </>
  );
};

const WorkspaceView = ({ kind }) => (
  <PmPage>
    {/* keyed: rows loaded for one view must never render with another view's layout */}
    <Inner key={kind} kind={kind} />
  </PmPage>
);

export default WorkspaceView;
